import { Download, FileQuestion } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatDateTime } from '../../../../lib/formatDate';
import { personnelApi } from '../personnelApi';
import type { PersonnelDocument } from '../personnelTypes';

/**
 * One filed document, shown at the size the screen allows, beside what is known about the file.
 *
 * **Everything security-bearing is `useBlobPreview`'s** — the VERIFIED type off the stored file,
 * the inert allow-list (PDF to the browser's own viewer, a raster image in an `<img>`) and the
 * re-typed blob. Nothing here decides what may render. The bytes come from the same endpoint
 * Download uses, behind `personnel.read` and the record's access level — there is no download
 * key to split here, because the panel naming the file already discloses it.
 *
 * **It writes nothing, so it carries no confirm tick** — `<ImagePreviewModal>`'s rule.
 *
 * **Mounting is what fetches** — it mounts only when a file's name is clicked.
 */
export function DocumentPreviewModal({
  personnelId,
  document,
  onDownload,
  onClose,
}: {
  personnelId: string;
  document: PersonnelDocument;
  onDownload: () => void;
  onClose: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => personnelApi.downloadDocument(personnelId, document.id, signal),
    [personnelId, document.id],
  );

  const { kind, url, loading, error } = useBlobPreview(document.id, document.contentType, load);

  return (
    <Modal
      open
      onClose={onClose}
      title={document.fileName}
      size="3xl"
      id="personnel-document-preview-modal"
      footer={
        <Button
          id="personnel-document-preview-download"
          variant="secondary"
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
          /* The only way to save the file once its name opens this, so it stays in the tab run. */
          tabIndex={0}
        >
          Download
        </Button>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {kind === 'none' ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg bg-slate-50">
              <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <EmptyState
                minHeight="min-h-0"
                message="This file cannot be shown here. Download it to open it."
              />
            </div>
          ) : loading ? (
            <Skeleton className="h-[55vh] w-full" />
          ) : error || !url ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-slate-50">
              <ErrorState message={error?.message ?? 'Could not load the preview.'} />
            </div>
          ) : kind === 'image' ? (
            <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
              {/* The uploaded name is what this image IS as far as the system knows — so it is the alt. */}
              <img
                src={url}
                alt={document.fileName}
                className="max-h-[55vh] w-auto max-w-full rounded object-contain"
              />
            </div>
          ) : (
            <iframe
              src={url}
              title={`Preview of ${document.fileName}`}
              className="h-[55vh] w-full rounded-lg border-0 bg-slate-50"
            />
          )}
        </div>

        {/* What the system knows about the file — read-only facts, so the field table. */}
        <div className="w-full shrink-0 lg:w-80">
          <PreviewTable
            rows={[
              /* No File name row — the modal's TITLE is the file name, and a value is printed once. */
              { label: 'Document', value: document.documentTypeName },
              { label: 'Type', value: document.contentType ?? '—', mono: document.contentType != null },
              {
                label: 'Size',
                value: document.sizeBytes != null ? formatBytes(document.sizeBytes) : '—',
                mono: document.sizeBytes != null,
              },
              { label: 'Created at', value: formatDateTime(document.createdAt), mono: true },
              { label: 'Created by', value: document.createdByName ?? '—' },

              /* A document never touched since filing has no update to state — and a replaced file IS one. */
              ...(document.updatedAt != null
                ? [
                    { label: 'Updated at', value: formatDateTime(document.updatedAt), mono: true },
                    { label: 'Updated by', value: document.updatedByName ?? '—' },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
